const DIRECT_BOOKING_HOLD_MINUTES = 10;
const DIRECT_HOLD_PREFIX = "ogestyle_direct_booking_hold:";
const CART_HOLD_KEY = "ogestyle_checkout_booking_holds";

export const BOOKING_HOLD_EXPIRED_MESSAGE =
  "This appointment hold has expired. Please select a time slot again before payment.";

const safeParseJson = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (_err) {
    return fallback;
  }
};

export const getHoldExpiresAt = (minutes = DIRECT_BOOKING_HOLD_MINUTES) => {
  return new Date(Date.now() + minutes * 60 * 1000).toISOString();
};

export const isHoldExpired = (expiresAt, now = Date.now()) => {
  if (!expiresAt) return true;
  const expiresAtMs = new Date(expiresAt).getTime();
  return !Number.isFinite(expiresAtMs) || expiresAtMs <= now;
};

export const buildDirectHoldKey = ({ shopId, staffId, serviceId, appointmentDate, startTime }) => {
  return `${DIRECT_HOLD_PREFIX}${[shopId, staffId, serviceId, appointmentDate, startTime]
    .map((part) => String(part || ""))
    .join(":")}`;
};

export const saveDirectBookingHold = (hold) => {
  if (!hold?.appointmentDate || !hold?.startTime) return null;
  const expiresAt = hold.expiresAt || getHoldExpiresAt();
  const payload = { ...hold, expiresAt };
  sessionStorage.setItem(buildDirectHoldKey(payload), JSON.stringify(payload));
  return payload;
};

export const getDirectBookingHold = (hold) => {
  const key = buildDirectHoldKey(hold || {});
  const payload = safeParseJson(sessionStorage.getItem(key), null);
  if (!payload) return null;
  if (isHoldExpired(payload.expiresAt)) {
    sessionStorage.removeItem(key);
    return null;
  }
  return payload;
};

export const clearDirectBookingHold = (hold) => {
  sessionStorage.removeItem(buildDirectHoldKey(hold || {}));
};

export const getCartBookingHolds = () => {
  const holds = safeParseJson(sessionStorage.getItem(CART_HOLD_KEY), {});
  if (!holds || typeof holds !== "object" || Array.isArray(holds)) return {};
  return holds;
};

export const saveCartBookingHold = (booking) => {
  const bookingId = String(booking?.bookingId || booking?._id || booking?.bookingData?._id || "");
  const reservationExpiresAt =
    booking?.reservationExpiresAt || booking?.bookingData?.reservationExpiresAt || booking?.expiresAt;
  if (!bookingId || !reservationExpiresAt) return null;

  const holds = getCartBookingHolds();
  holds[bookingId] = {
    bookingId,
    shopId: booking.shopId || booking.bookingData?.shopId || "",
    appointmentDate: booking.appointmentDate || booking.bookingData?.appointmentDate || "",
    startTime: booking.startTime || booking.bookingData?.startTime || "",
    reservationExpiresAt,
  };
  sessionStorage.setItem(CART_HOLD_KEY, JSON.stringify(holds));
  return holds[bookingId];
};

export const clearCartBookingHold = (bookingId) => {
  const normalizedId = String(bookingId || "");
  if (!normalizedId) return;
  const holds = getCartBookingHolds();
  delete holds[normalizedId];
  sessionStorage.setItem(CART_HOLD_KEY, JSON.stringify(holds));
};

export const pruneExpiredCartBookingHolds = () => {
  const holds = getCartBookingHolds();
  const activeHolds = {};
  const expiredBookingIds = [];

  Object.entries(holds).forEach(([bookingId, hold]) => {
    if (isHoldExpired(hold?.reservationExpiresAt)) {
      expiredBookingIds.push(bookingId);
      return;
    }
    activeHolds[bookingId] = hold;
  });

  sessionStorage.setItem(CART_HOLD_KEY, JSON.stringify(activeHolds));
  return expiredBookingIds;
};

export const findExpiredCartBookings = (bookings = []) => {
  const expiredFromStorage = new Set(pruneExpiredCartBookingHolds());
  return bookings.filter((booking) => {
    const bookingId = String(booking?.bookingId || booking?._id || booking?.bookingData?._id || "");
    const reservationExpiresAt =
      booking?.reservationExpiresAt || booking?.bookingData?.reservationExpiresAt || getCartBookingHolds()[bookingId]?.reservationExpiresAt;
    return bookingId && (expiredFromStorage.has(bookingId) || isHoldExpired(reservationExpiresAt));
  });
};
